import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import PageSeo from '../seo/PageSeo';
import PageLayout from '../components/PageLayout';
import { useAuth } from '../auth/AuthProvider';
import { getGoogleSignInErrorMessage, getOwnerPasswordErrorMessage } from '../auth/googleErrors';
import { SOCIAL_MAILTO } from '../site/contact';
import { isAdminEmail } from '../auth/config';
import { ADMIN_ON_OWNER_PAGE_MESSAGE, getOwnerSignInBlockReason } from '../auth/ownerAccess';
import {
    galiaAccentBarSx,
    galiaBodySx,
    galiaEyebrowSx,
    galiaFieldSx,
    galiaHeadlineSx,
    galiaPrimaryButtonSx,
    galiaSecondaryButtonSx,
    galiaSectionLabelSx,
} from '../theme/galiaUi';

const OWNER_PASSWORD_HELP_MAILTO = `${SOCIAL_MAILTO}?subject=${encodeURIComponent(
    'Puppy owner password help'
)}`;

const PuppyOwners: React.FC = () => {
    const {
        user,
        loading,
        firebaseReady,
        devAdminEnabled,
        signInWithGoogle,
        signInOwnerGoogle,
        signInOwnerEmail,
        continueAsDevAdmin,
        signOut,
    } = useAuth();
    const location = useLocation();
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [ownerEmail, setOwnerEmail] = useState('');
    const [ownerPassword, setOwnerPassword] = useState('');
    const [ownerNotice, setOwnerNotice] = useState<string | null>(null);
    const [ownerError, setOwnerError] = useState<string | null>(null);
    const showAdminSignIn = new URLSearchParams(location.search).has('admin');
    const fromPath =
        (location.state as { from?: string } | null)?.from &&
        !['/sign-in', '/puppy-owners'].includes((location.state as { from: string }).from)
            ? (location.state as { from: string }).from
            : '/admin';

    useEffect(() => {
        if (loading || showAdminSignIn || user?.role !== 'admin') {
            return;
        }

        setOwnerError(ADMIN_ON_OWNER_PAGE_MESSAGE);
        void signOut();
    }, [loading, showAdminSignIn, user, signOut]);

    if (!loading && showAdminSignIn && user?.role === 'owner') {
        return <Navigate to="/puppy-owners" replace />;
    }

    if (!loading && showAdminSignIn && user?.role === 'admin') {
        return <Navigate to={fromPath} replace />;
    }

    const handleGoogleSignIn = async () => {
        setError(null);
        setSubmitting(true);
        try {
            await signInWithGoogle();
        } catch (err) {
            setError(getGoogleSignInErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleOwnerGoogleSignIn = async () => {
        setOwnerError(null);
        setOwnerNotice(null);
        setSubmitting(true);
        try {
            await signInOwnerGoogle();
        } catch (err) {
            setOwnerError(getGoogleSignInErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    const handleOwnerSignIn = async (event: React.FormEvent) => {
        event.preventDefault();
        setOwnerNotice(null);
        setOwnerError(null);

        const email = ownerEmail.trim();
        const password = ownerPassword.trim();

        if (!email || !password) {
            setOwnerError('Please enter your email and password.');
            return;
        }

        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setOwnerError('Please enter a valid email address.');
            return;
        }

        if (isAdminEmail(email)) {
            setOwnerError(ADMIN_ON_OWNER_PAGE_MESSAGE);
            return;
        }

        if (!firebaseReady) {
            const blockReason = await getOwnerSignInBlockReason(email);
            if (blockReason) {
                setOwnerError(blockReason);
                return;
            }
            setOwnerNotice(
                'This email is on our invite list. Owner accounts are not connected yet, so sign-in will be available soon.'
            );
            return;
        }

        setSubmitting(true);
        try {
            await signInOwnerEmail(email, password);
        } catch (err) {
            setOwnerError(getOwnerPasswordErrorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageSeo
                title={
                    showAdminSignIn
                        ? 'Admin sign in | Galia Standard Poodles'
                        : 'Puppy owners | Galia Standard Poodles'
                }
                description="Private sign-in for Galia Standard Poodles."
                path="/puppy-owners"
                noIndex
            />
            <PageLayout>
                <Box sx={{ maxWidth: 440 }}>
                    <Typography component="p" sx={galiaEyebrowSx}>
                        Galia Standard Poodles
                    </Typography>
                    <Typography component="h1" sx={galiaHeadlineSx}>
                        {showAdminSignIn ? 'Sign in' : 'Puppy owners'}
                    </Typography>
                    <Box sx={galiaAccentBarSx} />
                    <Typography sx={{ ...galiaBodySx, mb: 3.5 }}>
                        {loading
                            ? 'Checking sign-in…'
                            : showAdminSignIn
                                ? 'Sign in to manage invitations and owner accounts.'
                                : 'Please sign in to view your puppy information.'}
                    </Typography>

                    {!showAdminSignIn && user?.role === 'owner' && (
                        <Stack spacing={2}>
                            <Alert severity="success" sx={{ borderRadius: 2 }}>
                                Signed in as {user.email}. The owner portal will be available here
                                soon.
                            </Alert>
                            <Button
                                variant="outlined"
                                onClick={() => void signOut()}
                                sx={{ ...galiaSecondaryButtonSx, width: '100%' }}
                            >
                                Sign out
                            </Button>
                        </Stack>
                    )}

                    {!showAdminSignIn && user?.role !== 'owner' && (
                        <Box component="section" aria-labelledby="owner-sign-in-heading">
                            <Typography
                                id="owner-sign-in-heading"
                                sx={{
                                    mb: 2.25,
                                    fontSize: '0.95rem',
                                    lineHeight: 1.5,
                                    color: 'rgba(26, 26, 26, 0.62)',
                                }}
                            >
                                Use the email and temporary password we sent you, or sign in with
                                Google using that same invited email.
                            </Typography>
                            {ownerError && (
                                <Alert
                                    severity="error"
                                    sx={{ mb: 2, borderRadius: 2 }}
                                    action={
                                        ownerError === ADMIN_ON_OWNER_PAGE_MESSAGE ? undefined : (
                                            <Button
                                                color="inherit"
                                                size="small"
                                                href={SOCIAL_MAILTO}
                                                sx={{ textTransform: 'none', fontWeight: 600 }}
                                            >
                                                Email us
                                            </Button>
                                        )
                                    }
                                >
                                    {ownerError}
                                </Alert>
                            )}
                            {ownerNotice && (
                                <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                                    {ownerNotice}
                                </Alert>
                            )}
                            <Stack
                                component="form"
                                spacing={1.75}
                                onSubmit={(event) => void handleOwnerSignIn(event)}
                            >
                                <TextField
                                    label="Email"
                                    type="email"
                                    value={ownerEmail}
                                    onChange={(event) => {
                                        setOwnerEmail(event.target.value);
                                        setOwnerError(null);
                                        setOwnerNotice(null);
                                    }}
                                    autoComplete="email"
                                    required
                                    fullWidth
                                    sx={galiaFieldSx}
                                />
                                <TextField
                                    label="Password"
                                    type="password"
                                    value={ownerPassword}
                                    onChange={(event) => {
                                        setOwnerPassword(event.target.value);
                                        setOwnerError(null);
                                        setOwnerNotice(null);
                                    }}
                                    autoComplete="current-password"
                                    required
                                    fullWidth
                                    sx={galiaFieldSx}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={submitting || loading}
                                    sx={{ ...galiaPrimaryButtonSx, width: '100%' }}
                                >
                                    Sign in
                                </Button>
                            </Stack>
                            <Typography
                                sx={{
                                    mt: 1.75,
                                    fontSize: '0.9rem',
                                    lineHeight: 1.5,
                                    color: 'rgba(26, 26, 26, 0.55)',
                                }}
                            >
                                Forgot your password?{' '}
                                <Link
                                    href={OWNER_PASSWORD_HELP_MAILTO}
                                    underline="always"
                                    sx={{ fontWeight: 600, color: 'inherit' }}
                                >
                                    Email us
                                </Link>
                                {' '}and we will send a new one.
                            </Typography>
                            <Typography
                                sx={{
                                    my: 2,
                                    textAlign: 'center',
                                    fontSize: '0.8125rem',
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: 'rgba(26, 26, 26, 0.4)',
                                }}
                            >
                                or
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => void handleOwnerGoogleSignIn()}
                                disabled={!firebaseReady || submitting || loading}
                                sx={{ ...galiaSecondaryButtonSx, width: '100%' }}
                            >
                                Sign in with Google
                            </Button>
                        </Box>
                    )}

                    {showAdminSignIn && (
                        <Box component="section" aria-labelledby="admin-sign-in-heading">
                            <Typography component="h2" id="admin-sign-in-heading" sx={galiaSectionLabelSx}>
                                Admin
                            </Typography>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Stack spacing={1.5}>
                                <Button
                                    variant="contained"
                                    onClick={() => void handleGoogleSignIn()}
                                    disabled={!firebaseReady || submitting || loading}
                                    sx={{ ...galiaPrimaryButtonSx, width: '100%' }}
                                >
                                    Sign in with Google
                                </Button>
                                {devAdminEnabled && (
                                    <Button
                                        variant="outlined"
                                        onClick={continueAsDevAdmin}
                                        disabled={loading}
                                        sx={{ ...galiaSecondaryButtonSx, width: '100%' }}
                                    >
                                        Continue as local admin
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    )}
                </Box>
            </PageLayout>
        </>
    );
};

export default PuppyOwners;
